import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
// Utils
import { Form, FormElements } from '@zolteam/zol-react-form';
// Components
import { Box, Button } from '@zolteam/zol-admin-ui';
// Services
import { postAdminUserPasswordReset } from '../../../services/parazol/parazolService';
// Locales
import fr from '../../../locales/fr.json';

const FORM_NAME = 'cortizol.forgotPassword';

const SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'forgotPassword',
  properties: {
    userEmailAddress: {
      type: 'string',
      format: 'email',
      minLength: 1,
      description: 'Email',
      x_layout: '1/1/1/13',
    },
  },
  required: [
    'userEmailAddress',
  ],
  additionalProperties: false,
};

/**
 * Forgot Password View
 * @returns {JSX.Element}
 */
function ForgotPassword() {
  // Hooks
  const { t } = useTranslation();

  // Services
  const {
    mutate: mutateForgotPassword,
    isLoading: isLoadingForgotPassword,
    isSuccess: success,
  } = useMutation({
    mutationFn: postAdminUserPasswordReset,
  });

  // Handlers
  /**
   * Submit the form.
   * @param {object} data
   * @param {string} data.userEmailAddress
   * @returns {void}
   */
  const onSubmit = (data) => {
    const url = `${import.meta.env.VITE_APP_URL}/reset-password?token=:token`;
    mutateForgotPassword({ ...data, passwordResetUrl: url });
  };

  return (
    <Box direction="column" gap="m" fullWidth>
      {success ? (
        <>
          <Box direction="column" gap="xs" align="center" fullWidth>
            <h1 className="h3-default font-weight-bold">
              {t('cortizol.forgotPassword.successTitle')}
            </h1>
            <span className="p1-default">
              {t('cortizol.forgotPassword.successDescription')}
            </span>
          </Box>

          <Box direction="column" gap="xs" align="normal" fullWidth>
            <Button color="noBackground">
              <Link to="/login">
                {t('cortizol.forgotPassword.login')}
              </Link>
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box direction="column" gap="xs" align="center" fullWidth>
            <h1 className="h3-default font-weight-bold">
              {t('cortizol.forgotPassword.title')}
            </h1>
            <span className="p1-default">
              {t('cortizol.forgotPassword.description')}
            </span>
          </Box>

          <Box fullWidth>
            <Form
              name={FORM_NAME}
              className=""
              callbacks={{
                onSubmit,
              }}
              schema={SCHEMA}
              translations={{ formValidation: fr.cortizol.formValidation }}
            >
              {(formConfig, formInfo) => (
                <FormElements
                  formConfig={formConfig}
                  formTitle={formInfo.formName}
                />
              )}
            </Form>
          </Box>

          <Box direction="column" gap="xs" align="normal" fullWidth>
            <Button
              isSubmit
              form={FORM_NAME}
              isLoading={isLoadingForgotPassword}
            >
              {t('cortizol.forgotPassword.submit')}
            </Button>

            <Button color="noBackground">
              <Link to="/login">
                {t('cortizol.forgotPassword.login')}
              </Link>
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default ForgotPassword;
